/** @module routes/Account/ProfileEdit */
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import { useStoreActions } from "app/utils/hooks";
import { fetchSports } from "app/actions/sports";
import downChevron from "app/images/down-chevron.svg";
import styles from "./styles.module.scss";

// same result cap the shared SelectBox/Base applies while filtering
const SHOW_RESULTS = 100;

interface PropsDef {
  inputId: string;
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
}

/**
 * Sport picker styled like the page's native fields: a plain text input (so
 * the theme's _fields.scss rules apply to it directly) with a suggestions
 * menu fed by the same /sports list SportSelect uses. Free text is a valid
 * value, matching SportSelect's isCreatable behavior.
 */
export default function SportInput(props: PropsDef) {
  const { inputId, value, onChange, hasError } = props;
  const { t } = useTranslation();
  // same fetch the shared SportSelect performs: dispatches /sports once and
  // reads state.sports (noAuth mirrors SportSelect's usage)
  const { sports } = useStoreActions({ sports: fetchSports }, { noAuth: true });

  const [isOpen, setIsOpen] = useState(false);
  // The menu filter is tracked separately from the value so reopening the
  // menu on a saved sport offers the full list, not just the current match.
  const [filter, setFilter] = useState("");
  // null = no active option; only typing, arrows or hover activate one, so a
  // stray Enter on a freshly focused field cannot overwrite the saved sport.
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  // gated on isOpen: the parent re-renders on every keystroke in the other
  // profile fields, and this result is only read while the menu is showing
  const options: string[] = useMemo(() => {
    if (!isOpen || sports.data == null) {
      return [];
    }

    const query = filter.toLowerCase();
    return sports.data
      .filter((sport: string) => sport.toLowerCase().includes(query))
      .slice(0, SHOW_RESULTS);
  }, [isOpen, filter, sports.data]);

  const menuOpen = isOpen && options.length > 0;
  const activeOption =
    menuOpen && highlighted != null ? options[highlighted] : undefined;

  const openMenu = (nextFilter = "") => {
    setFilter(nextFilter);
    // typing pre-activates the top match so Enter accepts it (react-select
    // behavior); plain focus or click activates nothing
    setHighlighted(nextFilter ? 0 : null);
    setIsOpen(true);
  };

  const pick = (sport: string) => {
    onChange(sport);
    setIsOpen(false);
  };

  const handleInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    onChange(evt.target.value);
    openMenu(evt.target.value);
  };

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (evt.key === "Enter") {
      if (activeOption != null) {
        evt.preventDefault();
        pick(activeOption);
      }
      return;
    }

    if (evt.key === "ArrowDown" || evt.key === "ArrowUp") {
      evt.preventDefault();

      if (!isOpen) {
        openMenu();
        return;
      }

      if (!options.length) {
        return;
      }

      let next: number;
      if (highlighted == null) {
        next = evt.key === "ArrowDown" ? 0 : options.length - 1;
      } else {
        const delta = evt.key === "ArrowDown" ? 1 : -1;
        next = (highlighted + delta + options.length) % options.length;
      }

      setHighlighted(next);
      menuRef.current?.children[next]?.scrollIntoView({ block: "nearest" });
    }
  };

  // close only when focus leaves the whole widget (same check as
  // SelectBox/Base's outer onBlur)
  const handleBlur = (evt: React.FocusEvent<HTMLDivElement>) => {
    const relatedTarget = evt.relatedTarget as HTMLElement;
    if (!evt.currentTarget.contains(relatedTarget)) {
      setIsOpen(false);
    }
  };

  return (
    <div className={styles.wrapper} onBlur={handleBlur}>
      <input
        type="text"
        id={inputId}
        role="combobox"
        aria-expanded={menuOpen}
        aria-autocomplete="list"
        aria-controls={menuOpen ? `${inputId}-menu` : undefined}
        aria-activedescendant={
          activeOption != null ? `${inputId}-option-${highlighted}` : undefined
        }
        autoComplete="off"
        className={cls(styles.input, { error: hasError })}
        placeholder={t("Select Sport / Subject")}
        value={value}
        onChange={handleInputChange}
        onFocus={() => openMenu()}
        onMouseDown={() => {
          // reopen after Escape while still focused; guarded so a click
          // inside an already-open menu's input keeps the current filter
          if (!isOpen) {
            openMenu();
          }
        }}
        onKeyDown={handleKeyDown}
      />
      <img src={downChevron} alt="" aria-hidden className={styles.chevron} />
      {menuOpen && (
        <ul
          id={`${inputId}-menu`}
          role="listbox"
          className={styles.menu}
          ref={menuRef}
        >
          {options.map((sport, index) => (
            <li
              key={sport}
              id={`${inputId}-option-${index}`}
              role="option"
              aria-selected={sport === value}
              className={cls(styles.option, {
                [styles.highlighted]: index === highlighted,
              })}
              // mousedown (not click) so the input never blurs mid-selection
              onMouseDown={(evt) => {
                evt.preventDefault();
                pick(sport);
              }}
              onMouseEnter={() => setHighlighted(index)}
            >
              {sport}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
